<script setup lang="ts">
  import { computed, onBeforeUnmount, ref } from 'vue';
  import { useCatalogPage } from '@/composition/catalog/catalog-page-context';
  import type { ICatalogPanel } from '@/composition/catalog/catalog-page-state';
  import {
    categoryMenuEntries,
    moreMenuEntries,
    rowMenuEntries,
  } from '@/domain/catalog/catalog-menus';
  import { IBulkPriceChange } from '@/interfaces/ICatalogV2';
  import { ICategory } from '@/interfaces/ICategory';
  import { IItem } from '@/interfaces/IItem';
  import { useRestaurantStore } from '@/stores/restaurant.store';
  import V2CatalogActionList from './V2CatalogActionList.vue';
  import V2CatalogAvailabilityChooser from './V2CatalogAvailabilityChooser.vue';
  import V2CatalogQuickPrice from './V2CatalogQuickPrice.vue';

  /**
   * What the page's floating panel shows (plan §5.9): the availability
   * chooser, «Cambiar precio», or a ⋯ menu, and what their buttons do.
   * The same content renders in the popover and in the phone sheet.
   */
  const props = defineProps<{
    panel: ICatalogPanel;
    /** The product it acts on (row panels). */
    item?: IItem | null;
    /** The category it acts on (category panels). */
    category?: ICategory | null;
    /** Rendered in the phone sheet (orange primary buttons). */
    sheet: boolean;
  }>();

  const {
    state,
    layout,
    data,
    access,
    online,
    actions,
    commands,
  } = useCatalogPage();
  const restaurantStore = useRestaurantStore();

  const branchName = computed(() => restaurantStore.selectedBranch?.name ?? '');
  const canViewMenu = computed(() => !!data.restaurant.value?.stringId);
  const showDisabledItems = computed(
    () => !!data.restaurant.value?.settings?.showDisabledItems
  );

  const rowEntries = computed(() =>
    rowMenuEntries(access.value, canViewMenu.value)
  );

  const categoryEntries = computed(() => {
    const id = props.category?.id;
    const items = data.items.value.filter((entry) => entry.categoryId === id);
    return categoryMenuEntries({
      access: access.value,
      itemCount: items.length,
      visibleCount: items.filter((entry) => entry.visible).length,
    });
  });

  const moreEntries = computed(() =>
    moreMenuEntries({
      access: access.value,
      tier: layout.tier.value,
      planTier: restaurantStore.effectiveTier,
      multipleMenus: data.multipleMenus.value,
      hasProducts: data.rows.value.length > 0,
      canReorder: data.canReorder.value,
      canViewMenu: canViewMenu.value,
    })
  );

  const priceSaving = ref(false);
  let unmounted = false;
  onBeforeUnmount(() => {
    unmounted = true;
  });

  async function onSavePrice(change: IBulkPriceChange | null) {
    const opened = state.panel;
    if (!change) {
      state.panel = null;
      return;
    }
    priceSaving.value = true;
    const saved = await actions.savePrice(change, {
      reveal: props.panel.kind === 'price' && !!props.panel.reveal,
    });
    // Closing unmounts this content: nothing may run after that.
    if (unmounted) return;
    priceSaving.value = false;
    // Only this panel: another one may have opened meanwhile.
    if (saved && state.panel === opened) state.panel = null;
  }

  /** Phone sheets have no anchor: the ⋯ says what it acts on. */
  const menuTarget = computed(() =>
    props.sheet &&
    (props.panel.kind === 'rowMenu' || props.panel.kind === 'categoryMenu')
      ? props.item?.name ?? props.category?.name ?? null
      : null
  );

  function onRowSelect(key: string) {
    const entry = rowEntries.value.find((candidate) => candidate.key === key);
    if (entry && props.item)
      commands.runRowCommand(entry.key, props.item, props.panel.anchor);
  }

  function onCategorySelect(key: string) {
    const entry = categoryEntries.value.find(
      (candidate) => candidate.key === key
    );
    if (entry && props.category)
      commands.runCategoryCommand(entry.key, props.category);
  }

  function onMoreSelect(key: string) {
    const entry = moreEntries.value.find((candidate) => candidate.key === key);
    if (entry) commands.runMoreCommand(entry.key);
  }
</script>

<template>
  <div class="v2-catalog-panel-content">
    <p v-if="menuTarget" class="content-target" :title="menuTarget">
      {{ menuTarget }}
    </p>
    <V2CatalogAvailabilityChooser
      v-if="panel.kind === 'availability' && (item || category)"
      :panel="panel"
      :name="item ? item.name : category ? category.name : ''"
      :branch-name="branchName"
      :show-disabled-items="showDisabledItems"
      :sheet="sheet"
      @choose="actions.chooseAvailability"
    />
    <V2CatalogQuickPrice
      v-else-if="panel.kind === 'price' && item"
      :key="`price-${item.id}`"
      :item="item"
      :currency="data.rowContext.value.currency"
      :country="data.rowContext.value.country"
      :sheet="sheet"
      :saving="priceSaving"
      :online="online"
      @save="onSavePrice"
      @cancel="state.panel = null"
    />
    <V2CatalogActionList
      v-else-if="panel.kind === 'rowMenu' && item"
      :entries="rowEntries"
      :online="online"
      @select="onRowSelect"
    />
    <V2CatalogActionList
      v-else-if="panel.kind === 'categoryMenu' && category"
      :entries="categoryEntries"
      :online="online"
      @select="onCategorySelect"
    />
    <V2CatalogActionList
      v-else-if="panel.kind === 'moreMenu'"
      :entries="moreEntries"
      :online="online"
      @select="onMoreSelect"
    />
  </div>
</template>

<style scoped lang="scss">
  .v2-catalog-panel-content {
    .content-target {
      @apply tw-m-0 tw-mb-2 tw-truncate tw-text-base tw-font-semibold tw-text-neutral-900;
    }
  }
</style>
